// @owners { team: patients }
import { useSyncExternalStore } from 'react';
import { Storage } from '~shared/mmkv';
import { staleTimes } from '~shared/queries/constants';
import { queries } from '~shared/queries/query-keys';
import { useQuery } from '~shared/react-query';
import { SCHEDULED_DOWNTIME_BYPASS_TOKEN_STORAGE_KEY } from '../constants';
import { isBypassed } from '../helpers/isBypassed';
import { isDowntimeActive } from '../helpers/isDowntimeActive';

type ScheduledDowntimeState =
  | { isActive: false }
  | { isActive: true; title: string; message: string; phoneNumber: string | undefined };

type UseScheduledDowntimeOptions = {
  readonly staleTime?: number;
  readonly refetchInterval?: number;
};

// Module-level for a stable reference -- useSyncExternalStore re-subscribes whenever
// subscribe/getSnapshot change identity, and neither depends on anything per-call.
const subscribeToBypassToken = (onStoreChange: () => void) => {
  const listener = Storage.addOnValueChangedListener((key) => {
    if (key === SCHEDULED_DOWNTIME_BYPASS_TOKEN_STORAGE_KEY) onStoreChange();
  });
  return () => {
    listener.remove();
  };
};
const getBypassTokenSnapshot = () => Storage.getString(SCHEDULED_DOWNTIME_BYPASS_TOKEN_STORAGE_KEY);

// Any non-success state (loading, retrying, or finally errored) falls through to inactive —
// this is the fail-open behavior, with no explicit error handling needed.
export const useScheduledDowntime = ({
  staleTime = staleTimes.fiveMinutes,
  refetchInterval,
}: UseScheduledDowntimeOptions = {}): ScheduledDowntimeState => {
  const { data, isSuccess } = useQuery({ ...queries.scheduledDowntime.fetchStatus, staleTime, refetchInterval });

  // Storage.getString isn't reactive, and refetching alone isn't reliable -- React Query can skip
  // re-rendering when the refetched data is unchanged. Subscribe directly instead.
  const persistedBypassToken = useSyncExternalStore(subscribeToBypassToken, getBypassTokenSnapshot);

  if (!isSuccess || !isDowntimeActive(data.starts_at, data.expires_at, new Date())) {
    return { isActive: false };
  }

  if (isBypassed(persistedBypassToken, data.bypass_token)) {
    return { isActive: false };
  }

  return {
    isActive: true,
    title: data.title,
    message: data.message,
    phoneNumber: data.phone_number,
  };
};
